import { AgGridReact, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import HeaderCellRenderer, {
  HeaderRendererParamProps,
} from "~/pages/FundAccounting/Entities/components/CellRenderers/Header";

const GlIntegrationDateRangeTooltip =
  "GL integration date range defines the first and last GL dates of the GL transactions for this update";

export const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  {
    field: "category",
    headerName: "Category",
    filter: true,
    menuTabs: ["filterMenuTab"],
    // this is necessary to make the row expandable to show detail grid
    cellRenderer: "agGroupCellRenderer",
  },
  {
    field: "glTransactionType",
    headerName: "GL transaction type",
    filter: true,
    menuTabs: ["filterMenuTab"],
  },
  {
    field: "glIntegrationDateRange",
    headerName: "GL integration date range",
    headerComponent: (params: HeaderRendererParamProps) => {
      return (
        <HeaderCellRenderer params={params} tooltipContent={GlIntegrationDateRangeTooltip} />
      );
    },
  },
  {
    field: "amount",
    headerName: "Amount",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
  },
  {
    field: "updateCount",
    headerName: "Update Count",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
    cellRenderer: function (params: any) {
      return params.value.showWarningIcon ? (
        <Flex css={{ width: "100%", alignItems: "center" }} gap={4}>
          <Icon type="exclamation-triangle" color={Colors.Warning600} />
          <Text color={Colors.Warning600}>{params.value.count}</Text>
        </Flex>
      ) : (
        params.value.count
      );
    },
  },
];

type UpdateCountType = {
  count: string;
  showWarningIcon: boolean;
};

export type ColumnItemType = {
  category: string;
  glTransactionType: string;
  glIntegrationDateRange: string;
  amount: string;
  updateCount: UpdateCountType;
  callRecords: DetailColumnItemType[];
};

export type DetailColumnItemType = {
  batchId: string;
  glDate: string;
  amount: string;
  effectiveDate: string;
};

const masterDetailColumnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  {
    field: "batchId",
    headerName: "Batch ID",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
  },
  {
    field: "glDate",
    headerName: "GL date",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
  },
  {
    field: "effectiveDate",
    headerName: "Effective date",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
  },
  {
    field: "amount",
    headerName: "Amount",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
  },
  {
    field: "updateSummary",
    headerName: "Update summary",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} />;
    },
  },
];

export const gridOptions = (
  numberOfRows: number,
): AgGridReact.AgGridReactProps["gridOptions"] => {
  const paginationNumberOfRows = 10;
  return {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    suppressMenuHide: true,
    domLayout: "autoHeight",
    detailRowAutoHeight: true,
    // This is for better place the action button
    rowHeight: 44,
    pagination: numberOfRows > paginationNumberOfRows,
    paginationPageSizeSelector: false,
    paginationPageSize: paginationNumberOfRows,
  };
};

// specify which rows to expand
export const isRowMaster = (dataItem: ColumnItemType) => {
  return dataItem ? dataItem.callRecords.length > 0 : false;
};

// provide Detail Cell Renderer Params
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const detailCellRendererParams = (params: any) => {
  const paginationNumberOfRows = 10;
  return {
    // provide the Grid Options to use on the Detail Grid
    detailGridOptions: {
      defaultColDef: {
        flex: 1,
      },
      columnDefs: masterDetailColumnDefs,
      pagination:
        params.data &&
        params.data.callRecords &&
        params.data.callRecords.length > paginationNumberOfRows
          ? true
          : false,
      paginationPageSizeSelector: false,
      paginationPageSize: paginationNumberOfRows,
    },
    // get the rows for each Detail Grid
    // This is how the AG Grid types this function.
    // See: https://www.ag-grid.com/react-data-grid/master-detail-master-rows/#dynamic-master-rows
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDetailRowData: (params: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      params.successCallback(params.data.callRecords);
    },
  };
};

// TODO: This is a mock data. Replace with actual data from the backend
export const mockData: ColumnItemType[] = [
  {
    category: "Net income",
    glTransactionType: "R-Unrealized gains",
    glIntegrationDateRange: "Jan 1, 2020 - Mar 31, 2024",
    amount: "$100,000",
    updateCount: {
      count: "0",
      showWarningIcon: false,
    },
    // not master row
    callRecords: [],
  },
  {
    category: "Net income",
    glTransactionType: "R-Unrealized gains",
    glIntegrationDateRange: "Apr 1, 2020 - June 30, 2024",
    amount: "$100,000",
    updateCount: {
      count: "12",
      showWarningIcon: false,
    },
    callRecords: [
      {
        batchId: "000001",
        glDate: "Jan 1, 2020",
        amount: "$10,000",
        effectiveDate: "Jan 1, 2020",
      },
      {
        batchId: "000002",
        glDate: "Jan 23, 2024",
        amount: "$50,000",
        effectiveDate: "Jan 23, 2024",
      },
      {
        batchId: "000003",
        glDate: "Feb 2, 2024",
        amount: "$10,000",
        effectiveDate: "Feb 2, 2024",
      },
      {
        batchId: "000004",
        glDate: "Mar 31, 2024",
        amount: "$40,000",
        effectiveDate: "Mar 31, 2024",
      },
      {
        batchId: "000005",
        glDate: "Apr 1, 2024",
        amount: "$50,000",
        effectiveDate: "Apr 1, 2024",
      },
      {
        batchId: "00006",
        glDate: "Apr 24, 2024",
        amount: "$60,000",
        effectiveDate: "Apr 24, 2024",
      },
      {
        batchId: "000007",
        glDate: "May 4, 2024",
        amount: "$70,000",
        effectiveDate: "May 4, 2024",
      },
      {
        batchId: "000008",
        glDate: "May 13, 2024",
        amount: "$80,000",
        effectiveDate: "May 13, 2024",
      },
      {
        batchId: "000009",
        glDate: "Jun 1, 2024",
        amount: "$90,000",
        effectiveDate: "Jun 1, 2024",
      },
      {
        batchId: "000010",
        glDate: "Jun 16, 2024",
        amount: "$100,000",
        effectiveDate: "Jun 16, 2024",
      },
      {
        batchId: "000011",
        glDate: "Jul 3, 2024",
        amount: "$110,000",
        effectiveDate: "Jul 3, 2024",
      },
      {
        batchId: "000012",
        glDate: "Aug 21, 2024",
        amount: "$120,000",
        effectiveDate: "Aug 21, 2024",
      },
    ],
  },
  {
    category: "Net income",
    glTransactionType: "R-Unrealized gains",
    glIntegrationDateRange: "Jun 1, 2020 - Sep 30, 2024",
    amount: "$200,000",
    updateCount: {
      count: "4",
      showWarningIcon: false,
    },
    callRecords: [
      {
        batchId: "000001",
        glDate: "Jan 1, 2020",
        amount: "$10,000",
        effectiveDate: "Jan 1, 2020",
      },
      {
        batchId: "000002",
        glDate: "Jan 23, 2024",
        amount: "$50,000",
        effectiveDate: "Jan 23, 2024",
      },
      {
        batchId: "000003",
        glDate: "Feb 2, 2024",
        amount: "$10,000",
        effectiveDate: "Feb 2, 2024",
      },
      {
        batchId: "000004",
        glDate: "Mar 31, 2024",
        amount: "$40,000",
        effectiveDate: "Mar 31, 2024",
      },
    ],
  },
];
